// ---------------------------------------------------------------------------
// Workflow & RBAC types — matches rbac.ts + approval-workflow.ts
// ---------------------------------------------------------------------------

import type { TemplateStatus } from "./template";

// 'No Access' is the fail-closed fallback for a logged-in user whose roleCode
// maps to no survey role (not admin/staff, not broad, not a client reviewer).
// It grants nothing — no create, no manage, no approval transitions. It is NOT
// a synonym for Health Plan, which holds the privileged final-approval power.
export type CmsRole = "TCM Staff" | "TCM Admin" | "Health Plan" | "No Access";

export interface ApprovalWorkflow {
  workflow_id: string;
  template_id: string;
  actor_id: string;
  role: CmsRole;
  from_status: TemplateStatus;
  to_status: TemplateStatus;
  comment: string | null;
  created_on: string;
}

export interface ReviewComment {
  comment_id: string;
  template_id: string;
  author_id: string;
  author_role: CmsRole;
  comment_text: string;
  workflow_id: string | null;
  created_on: string;
  modified_on: string | null;
  deleted_on: string | null;
}

// RBAC transition matrix (mirrors backend src/shared/rbac.ts)
interface StatusTransition {
  from: TemplateStatus;
  to: TemplateStatus;
}

const TRANSITION_MATRIX: Record<CmsRole, StatusTransition[]> = {
  "TCM Staff": [{ from: "draft", to: "pending_internal_review" }],
  "TCM Admin": [
    { from: "pending_internal_review", to: "tcm_admin_approved" },
    { from: "pending_internal_review", to: "draft" },
    { from: "tcm_admin_approved", to: "pending_client_review" },
    { from: "revision_requested", to: "draft" },
  ],
  "Health Plan": [
    { from: "pending_client_review", to: "live" },
    { from: "pending_client_review", to: "revision_requested" },
  ],
  // No survey role → no transitions.
  "No Access": [],
};

/**
 * CMS-03a: per-role status set that defines a reviewer's "queue".
 * Used by ApprovalQueue.tsx and the sidebar badge count.
 */
export const REVIEW_QUEUE_STATUSES: Partial<Record<CmsRole, TemplateStatus[]>> =
  {
    "TCM Admin": ["pending_internal_review", "revision_requested"],
    // Health Plan queue is CMS-03b — gated on accessibleRolesCode follow-up.
    "Health Plan": ["pending_client_review"],
  };

/**
 * Whether a transition requires a comment. Per CMS-03a walked decision:
 * comment optional on approve, required on request-revisions.
 */
export function isCommentRequired(toStatus: TemplateStatus): boolean {
  return toStatus === "revision_requested" || toStatus === "draft";
}

export function getNextStatuses(
  role: CmsRole,
  currentStatus: TemplateStatus,
): TemplateStatus[] {
  const allowed = TRANSITION_MATRIX[role] ?? [];
  return allowed.filter((t) => t.from === currentStatus).map((t) => t.to);
}

export function canCreateTemplate(role: CmsRole): boolean {
  return role === "TCM Staff" || role === "TCM Admin";
}

export function canCreateQuestion(role: CmsRole): boolean {
  return role === "TCM Admin";
}

/**
 * Edit + delete of questions and question-answers. CMS-02 — TCM Admin only,
 * mirroring canCreateQuestion. Symmetric with the backend canManageQuestion
 * in src/shared/rbac.ts.
 */
export function canManageQuestion(role: CmsRole): boolean {
  return role === "TCM Admin";
}

// -- Transition / comment request shapes (Sprint 2) --

export interface TransitionRequest {
  toStatus: TemplateStatus;
  comment?: string;
}

export interface TransitionResponse {
  templateId: string;
  fromStatus: TemplateStatus;
  toStatus: TemplateStatus;
  workflowId: string;
  allowedNextStatuses: TemplateStatus[];
}

export interface CreateCommentRequest {
  commentText: string;
}

export interface UpdateCommentRequest {
  commentText: string;
}

/** Human-readable label for a status transition button */
export function transitionLabel(status: TemplateStatus): string {
  const labels: Record<TemplateStatus, string> = {
    draft: "Return to Draft",
    pending_internal_review: "Submit for Review",
    tcm_admin_approved: "Approve",
    pending_client_review: "Send to Client",
    live: "Approve & Publish",
    revision_requested: "Request Revision",
  };
  return labels[status];
}
